/* eslint-disable max-lines-per-function */
import {
  defineStandardChartLayer,
  type StandardChartLayerSpec,
  type Layer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'drillableTreemap',
  name_zh: '下钻矩形树图',
  name_en: 'Drillable Treemap',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    data: amount => amount > 1,
    x: amount => amount > 1,
    y: amount => amount > 1,
    group: amount => amount > 1
  },
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    group: {
      itemType: [DEFINE.DATE],
      category: DEFINE.SINGLE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    group: string;
    // TODO
    // 2024-12-03 应产品要求，需要将tooltip中待显示的原始数据字段名传入，在组件库内部调用部门的数据格式处理方法
    // 所以需要添加tooltip显示的数据字段名
    // tooltip: ['yoy', 'ratio']
    // tooltip: string[];
  };
}

function createStorage<V>(name: string): [() => V | undefined, (value: V) => Map<string, V>] {
  const map = new Map<string, V>();
  const get = () => map.get(name);
  const set = (value: V) => map.set(name, value);
  return [get, set];
}

export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  withTimeline: true,
  visInfo,

  render(api, spec) {
    const { data: dataArray, util, hook } = api;
    const presetOption = hook.buildStandardChartPresetOption(api);
    // 初始矩形块的数据索引
    const DEFAULT_DATA_INDEX_OF_VIEW_ROOT = 0;
    const {
      dataIndex = 0,
      encoding: { x, y, group: groupBy }
    } = spec;
    const levelToken = api.token.dvTreemapLevelStyle;
    const data = dataArray[dataIndex].values;
    const dataMetaInfo = hook.parseDataMetaInfo(dataArray[dataIndex]);
    // 当前视图下钻到的矩形块对应的数据索引的get/set方法
    const [dataIndexOfViewRoot, setDataIndexOfViewRoot] =
      createStorage<number>('dataIndexOfViewRoot');
    // 设定初值
    setDataIndexOfViewRoot(DEFAULT_DATA_INDEX_OF_VIEW_ROOT);
    // * ---------------------- data
    const { years } = util.dataEncoding.encoding(data, {
      years: {
        field: groupBy,
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      }
    });

    const getSeriesData = (datum, idx) => {
      // 原始数据的树深度
      let depth = 1;
      const dataItem = datum.filter(item => item[groupBy] === years[idx]);
      const res = dataItem.map((item, index) => {
        function parseChildren(children) {
          depth += 1;
          return children?.map((child, _) => ({
            ...item,
            name: child.name,
            value: +child[y],
            children: parseChildren(child.children)
          })) as [];
        }

        return {
          ...item,
          colorSaturation: index === 2 ? [0.45, 0.35] : [0.65, 0.55],
          name: item[x],
          value: +item[y],
          itemStyle: {
            color: presetOption?.color?.[index % presetOption.color.length]
          },
          children: parseChildren(item.children)
        };
      });
      return {
        res,
        depth
      };
    };

    // * ---------------------- chart

    const getLevels = (depth: number) => {
      const defaultLevels = [
        {
          itemStyle: {
            gapWidth: 2
          },
          upperLabel: {
            show: false
          }
        },
        {
          itemStyle: {
            borderColor: levelToken?.level_2.itemStyleBorderColor || '#EBf0FA',
            borderWidth: 4,
            gapWidth: 4
          },
          emphasis: {
            upperLabel: {
              backgroundColor: levelToken?.level_2.upperLabelBackgroundColorE || '#E1E6F0'
            },
            itemStyle: {
              borderColor: levelToken?.level_2.upperLabelBackgroundColorE || '#E1E6F0'
            }
          },
          upperLabel: {
            show: true,
            color: levelToken?.labelColor || 'rgba(0, 0, 0, 1)',
            backgroundColor: levelToken?.level_2.upperLabelBackgroundColor || '#EBf0FA',
            fontWeight: 600,
            height: 22,
            lineHeight: 22,
            verticalAlign: 'middle',
            formatter: params => {
              console.log('params: ', params);
              const result = hook.formatDataNumberValue(params.data.value);
              const unit = dataMetaInfo[y]?.unit ? dataMetaInfo[y]?.unit : '';
              return `{name|${params.data.name}：${result.value + result.unit + unit}}{${
                dataIndexOfViewRoot() === params.dataIndex ? 'close' : 'open'
              }|}`;
            },
            rich: {
              name: {
                padding: [5, 0, 0, 4]
              },
              close: {
                width: 16,
                height: 16,
                backgroundColor: {
                  image:
                    levelToken?.labelCloseIcon ||
                    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwXzEzNDBfMTkwNjkpIj4KPHBhdGggZD0iTTEzIDJDMTMuNTUyMyAyIDE0IDIuNDQ3NzIgMTQgM1YxM0MxNCAxMy41NTIzIDEzLjU1MjMgMTQgMTMgMTRIM0MyLjQ0NzcyIDE0IDIgMTMuNTUyMyAyIDEzVjNDMiAyLjQ0NzcyIDIuNDQ3NzIgMiAzIDJIMTNaTTEzIDNIM1YxM0gxM1YzWk0xMiA5VjEwSDEwVjEySDlWOUgxMlpNNyA0VjdINFY2SDZWNEg3WiIgZmlsbD0iYmxhY2siIGZpbGwtb3BhY2l0eT0iMC44NCIvPgo8L2c+CjxkZWZzPgo8Y2xpcFBhdGggaWQ9ImNsaXAwXzEzNDBfMTkwNjkiPgo8cmVjdCB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIGZpbGw9IndoaXRlIi8+CjwvY2xpcFBhdGg+CjwvZGVmcz4KPC9zdmc+Cg=='
                },
                align: 'right',
                verticalAlign: 'middle'
              },
              open: {
                width: 16,
                height: 16,
                backgroundColor: {
                  image:
                    levelToken?.labelOpenIcon ||
                    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTExIDVWN0gxMlY0SDlWNUgxMVoiIGZpbGw9ImJsYWNrIiBmaWxsLW9wYWNpdHk9IjAuODQiLz4KPHBhdGggZD0iTTUgMTFWOUg0VjEySDdWMTFINVoiIGZpbGw9ImJsYWNrIiBmaWxsLW9wYWNpdHk9IjAuODQiLz4KPHBhdGggZD0iTTEzIDJDMTMuNTUyMyAyIDE0IDIuNDQ3NzIgMTQgM1YxM0MxNCAxMy41NTIzIDEzLjU1MjMgMTQgMTMgMTRIM0MyLjQ0NzcyIDE0IDIgMTMuNTUyMyAyIDEzVjNDMiAyLjQ0NzcyIDIuNDQ3NzIgMiAzIDJIMTNaTTEzIDNIM1YxM0gxM1YzWiIgZmlsbD0iYmxhY2siIGZpbGwtb3BhY2l0eT0iMC44NCIvPgo8L3N2Zz4K'
                },
                align: 'right',
                verticalAlign: 'middle'
              }
            }
          }
        },
        {
          // colorSaturation: [0.35, 0.5],
          itemStyle: {
            borderWidth: 0,
            gapWidth: 1
          },
          emphasis: {
            upperLabel: {
              backgroundColor: levelToken?.level_3.upperLabelBackgroundColorE || '#CED6E3'
            }
          },
          upperLabel: {
            show: true,
            backgroundColor: levelToken?.level_3.upperLabelBackgroundColor || '#D5DEEB',
            color: levelToken?.labelColor || '#000',
            height: 22,
            lineHeight: 22,
            fontWeight: 400,
            verticalAlign: 'middle',
            formatter: params => {
              const result = hook.formatDataNumberValue(params.data.value);
              const unit = dataMetaInfo[y]?.unit ? dataMetaInfo[y]?.unit : '';
              return `{name|${params.data.name}：${result.value + result.unit + unit}}{${
                dataIndexOfViewRoot() === params.dataIndex ? 'close' : 'open'
              }|}`;
            },
            rich: {
              name: {
                padding: [5, 0, 0, 4]
              },
              close: {
                width: 16,
                height: 16,
                backgroundColor: {
                  image:
                    levelToken?.labelCloseIcon ||
                    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwXzEzNDBfMTkwNjkpIj4KPHBhdGggZD0iTTEzIDJDMTMuNTUyMyAyIDE0IDIuNDQ3NzIgMTQgM1YxM0MxNCAxMy41NTIzIDEzLjU1MjMgMTQgMTMgMTRIM0MyLjQ0NzcyIDE0IDIgMTMuNTUyMyAyIDEzVjNDMiAyLjQ0NzcyIDIuNDQ3NzIgMiAzIDJIMTNaTTEzIDNIM1YxM0gxM1YzWk0xMiA5VjEwSDEwVjEySDlWOUgxMlpNNyA0VjdINFY2SDZWNEg3WiIgZmlsbD0iYmxhY2siIGZpbGwtb3BhY2l0eT0iMC44NCIvPgo8L2c+CjxkZWZzPgo8Y2xpcFBhdGggaWQ9ImNsaXAwXzEzNDBfMTkwNjkiPgo8cmVjdCB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIGZpbGw9IndoaXRlIi8+CjwvY2xpcFBhdGg+CjwvZGVmcz4KPC9zdmc+Cg=='
                },
                align: 'right'
              },
              open: {
                width: 16,
                height: 16,
                backgroundColor: {
                  image:
                    levelToken?.labelOpenIcon ||
                    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTExIDVWN0gxMlY0SDlWNUgxMVoiIGZpbGw9ImJsYWNrIiBmaWxsLW9wYWNpdHk9IjAuODQiLz4KPHBhdGggZD0iTTUgMTFWOUg0VjEySDdWMTFINVoiIGZpbGw9ImJsYWNrIiBmaWxsLW9wYWNpdHk9IjAuODQiLz4KPHBhdGggZD0iTTEzIDJDMTMuNTUyMyAyIDE0IDIuNDQ3NzIgMTQgM1YxM0MxNCAxMy41NTIzIDEzLjU1MjMgMTQgMTMgMTRIM0MyLjQ0NzcyIDE0IDIgMTMuNTUyMyAyIDEzVjNDMiAyLjQ0NzcyIDIuNDQ3NzIgMiAzIDJIMTNaTTEzIDNIM1YxM0gxM1YzWiIgZmlsbD0iYmxhY2siIGZpbGwtb3BhY2l0eT0iMC44NCIvPgo8L3N2Zz4K'
                },
                align: 'right'
              }
            }
          }
        }
      ];
      const otherLavel = {
        // colorSaturation: [0.35, 0.5],
        itemStyle: {
          borderWidth: 0,
          gapWidth: 1,
          backgroundColor: 'rgba(224, 228, 234, 1)'
        },
        upperLabel: {
          show: false
        }
      };
      const needOtherLavel = depth > defaultLevels.length;
      return [
        ...defaultLevels.slice(0, Math.min(depth, defaultLevels.length)),
        ...(needOtherLavel ? Array.from(Array(depth - defaultLevels.length), () => otherLavel) : [])
      ];
    };
    const chartOptions = years.map((_, index) => {
      const { res: seriesData, depth: dataDepth } = getSeriesData(data, index);
      return {
        tooltip: {
          trigger: 'item',
          formatter: params => {
            const series = Array.isArray(params) ? params : [params];
            const items = [];

            series.forEach(item => {
              const result = hook.formatDataNumberValue(item.value);

              items.push({
                type: util.const.SERIES_2_LEGEND_TYPE_MAP[item.seriesType],
                color: item.color,
                name: item.name,
                value: result.value + result.unit
              });
            });

            return hook.buildTooltip({
              config: hook.buildStandardChartPresetOption(api).tooltip,
              title: '',
              items
            });
          }
        },
        xAxis: [],
        yAxis: [],
        series: [
          {
            type: 'treemap',
            roam: false,
            nodeClick: false,
            width: '100%',
            height: '100%',
            emphasis: {
              itemStyle: {
                // colorSaturation: 0.9,
                // color: 'red',
                colorAlpha: 0
              }
            },
            label: {
              formatter: params => {
                const result = hook.formatDataNumberValue(params.data.value, {
                  type: 'table',
                  unit: dataMetaInfo[y]?.unit
                });
                const unit = dataMetaInfo[y]?.unit ? dataMetaInfo[y]?.unit : '';
                return `{name|${params.data.name}}\n{value|${result.value + result.unit + unit}}`;
              },
              rich: {
                name: {
                  align: 'center',
                  color: '#fff',
                  fontSize: '16',
                  dvMinFontSize: '12',
                  dvLineHeight: 22 / 16
                },
                value: {
                  align: 'center',
                  color: '#fff',
                  fontSize: '14',
                  dvMinFontSize: '12',
                  dvLineHeight: 18 / 14,
                  dvValueFontDeviation: 3
                }
              }
            },
            dvLabelShowTimer: 50,
            dvLabelType: 'twoLineCenter',
            breadcrumb: {
              show: false
            },
            levels: getLevels(dataDepth),
            data: seriesData
          }
        ]
      };
    });
    return renderApi => {
      const optionParams = {
        option: renderApi.mergeChartOptionProxy(chartOptions[chartOptions.length - 1])
      };
      const timelineIns = renderApi.renderTimelineProxy(years as string[]);
      const chartIns = renderApi.renderChartProxy(optionParams);
      timelineIns.on('change', e => {
        optionParams.option = renderApi.mergeChartOptionProxy(chartOptions[e.index]);
        api.hook.beforeRenderStandardChart(api, optionParams);
        renderApi.optionCallbackProxy(optionParams.option);

        setDataIndexOfViewRoot(0);
        chartIns.play(optionParams);

        renderApi.cacheRenderOptionProxy(optionParams.option);
      });
      // 事件绑定时机：chartIns第一次完成渲染之后，ecIns已经实例化并且内部seriesData完成计算和数据记录
      chartIns.once('dv:afterupdate', () => {
        const ecIns = chartIns.getECharts();
        ecIns.on('click', { seriesIndex: 0 }, e => {
          const treeMapModel = ecIns.getModel().getSeriesByIndex(0);
          const treeMapData = treeMapModel.getData();
          const treeMapRoot = treeMapData.tree.root;
          const isLeafNode = !e?.data?.children?.length || e?.data?.children?.length < 2;
          if (isLeafNode) {
            return;
          }
          const targetNode =
            e.dataIndex === dataIndexOfViewRoot()
              ? treeMapRoot
              : treeMapData.tree.getNodeByDataIndex(e.dataIndex);
          setDataIndexOfViewRoot(
            e.dataIndex === dataIndexOfViewRoot() ? 0 : (e.dataIndex as number)
          );
          ecIns.dispatchAction({
            type: 'treemapRootToNode',
            seriesIndex: 0,
            targetNode
          });
        });
      });
    };
  }
});
